import { useReducer } from "react";
import moment from "moment-timezone";
import { ChevronDown, ChevronRight, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Activity } from "@/lib/api/customer";
import { CommonDestinationTimezone } from "@/lib/api/document";
import { TodoContextLine } from "./TodoContextLine";
import { TodoTranscriptContent, useTodoTranscript } from "./TodoTranscript";

interface TodoDetailsDialogProps {
  todo: Activity | null;
  onClose: () => void;
}

interface DialogState {
  isFlagOpen: boolean;
  isTranscriptOpen: boolean;
}

type DialogAction =
  | { type: "flagSectionToggled"; isOpen: boolean }
  | { type: "transcriptToggled"; isOpen: boolean };

// Both sections start expanded.
const initialState: DialogState = { isFlagOpen: true, isTranscriptOpen: true };

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case "flagSectionToggled":
      return { ...state, isFlagOpen: action.isOpen };
    case "transcriptToggled":
      return { ...state, isTranscriptOpen: action.isOpen };
  }
}

// Keying on the todo id remounts the dialog, so both sections start expanded
// each time a different todo is opened.
export function TodoDetailsDialog({ todo, onClose }: TodoDetailsDialogProps) {
  return (
    <TodoDetailsDialogContent
      key={todo?.id ?? "closed"}
      todo={todo}
      onClose={onClose}
    />
  );
}

/** Read-only: the flag reason (if flagged) and the call transcript. */
function TodoDetailsDialogContent({ todo, onClose }: TodoDetailsDialogProps) {
  const isOpen = todo !== null;
  const [{ isFlagOpen, isTranscriptOpen }, dispatch] = useReducer(
    dialogReducer,
    initialState,
  );

  // Rows without call info predate it; let the transcript request decide.
  const hasTranscript = todo?.Call ? todo.Call.has_transcript : true;
  // Fetched only while the dialog is open.
  const transcriptState = useTodoTranscript(todo?.id ?? "", isOpen && hasTranscript);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex h-[calc(100vh-5vw)] w-[95vw] max-w-[95vw] flex-col sm:h-[calc(100vh-10vw)] sm:max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>Todo Details</DialogTitle>
          <TodoContextLine todo={todo} />
          <DialogDescription>
            The flag reason, if any, and the call this Todo was created from.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4">
          {todo?.flagged_date && (
            <Collapsible
              open={isFlagOpen}
              onOpenChange={(isOpen) =>
                dispatch({ type: "flagSectionToggled", isOpen })
              }
              className="flex shrink-0 flex-col rounded-md border"
              data-testid="todo-details-flag"
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  data-testid="todo-details-flag-toggle"
                  className="flex w-full flex-wrap items-center gap-2 p-3 text-left text-sm font-medium hover:bg-muted/40"
                >
                  {isFlagOpen ? (
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  )}
                  Flag Reason
                  <span className="flex items-center gap-1 text-xs font-semibold text-red-700">
                    <Flag className="h-3.5 w-3.5" aria-hidden="true" />
                    Flagged
                    <time dateTime={todo.flagged_date} className="font-normal text-red-700/80">
                      {moment
                        .tz(todo.flagged_date, CommonDestinationTimezone)
                        .format("MMM Do YYYY, h:mm A z")}
                    </time>
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="border-t p-3">
                <p className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-red-200 bg-red-50 p-3 text-sm">
                  {todo.flagged_reason || "No reason was recorded."}
                </p>
              </CollapsibleContent>
            </Collapsible>
          )}

          <Collapsible
            open={isTranscriptOpen}
            onOpenChange={(isOpen) =>
              dispatch({ type: "transcriptToggled", isOpen })
            }
            className="flex flex-col rounded-md border data-[state=open]:min-h-[16rem] data-[state=open]:flex-1"
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                data-testid="todo-details-transcript-toggle"
                className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium hover:bg-muted/40"
              >
                {isTranscriptOpen ? (
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                )}
                Call Transcript
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="min-h-0 flex-1 flex-col border-t p-3 data-[state=open]:flex">
              {hasTranscript ? (
                <TodoTranscriptContent {...transcriptState} />
              ) : (
                <div className="flex min-h-48 items-center justify-center rounded-md bg-muted/20 p-4 text-sm text-muted-foreground">
                  No transcript is available for this Todo.
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
